import { endOfDayUTC } from '@/lib/date-utils';

/**
 * Quick-pick durations exposed by the alert Mute and Exclude dropdowns.
 *
 * Threaded into analytics payloads so we can measure which presets users
 * prefer. Both buttons share this union to keep the Mixpanel event vocabulary
 * coherent across mute and exclude.
 */
export type DurationPreset =
  | 'forever'
  | '1_day'
  | '1_week'
  | '1_month'
  | 'custom';

/**
 * Single source of truth for the dropdown menu labels rendered by both
 * `AlertMuteButton` and `AlertExcludeButton`. Hoisted here so a label tweak
 * (or a new preset) lands in exactly one place. Typed as
 * `Readonly<Record<DurationPreset, string>>` so adding a value to the
 * `DurationPreset` union forces a compile-time entry here.
 *
 * Labels are display-only — they are NEVER sent to the backend (the only
 * value that crosses the wire is the resolved `mutedUntil` / `excludedUntil`
 * ISO produced by the helpers below). Keep them out of `@kirin/api-client`.
 */
export const DURATION_DROPDOWN_LABELS: Readonly<
  Record<DurationPreset, string>
> = {
  forever: 'Forever',
  '1_day': '1 day',
  '1_week': '1 week',
  '1_month': '1 month',
  custom: 'Custom...',
};

/**
 * Returns an ISO datetime `n` days from "now" (truncated to UTC end-of-day on
 * the target day). Using end-of-day UTC means a "1 day" mute or exclusion
 * reliably covers the rest of today and tomorrow regardless of the user's
 * local timezone.
 */
export function untilDays(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + days);
  return endOfDayUTC(d);
}

/**
 * Returns an ISO datetime exactly one calendar month from "now" (UTC), with
 * the standard JS overflow rule (e.g. Jan 31 → Mar 3). Uses real month math
 * — not a fixed 30-day window — so the "1 month" label matches user
 * expectations across months of varying length.
 */
export function untilOneMonth(): string {
  const d = new Date();
  d.setUTCMonth(d.getUTCMonth() + 1);
  return endOfDayUTC(d);
}

/**
 * Earliest selectable day for the custom calendar: tomorrow at UTC start, so
 * the user cannot pick "today" (which would resolve to a `*Until` whose
 * end-of-day is already in the past for late-day local timezones).
 */
export function tomorrowUtcStart(): Date {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + 1);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

export { endOfDayUTC };
